import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Conversation, Message, MessageInput } from "@/types/chat";

export const useConversations = () => {
  const queryClient = useQueryClient();
  const currentEmployeeId = localStorage.getItem('currentEmployeeId');

  const { data: conversations = [], isLoading, error } = useQuery({
    queryKey: ['conversations', currentEmployeeId],
    queryFn: async (): Promise<Conversation[]> => {
      if (!currentEmployeeId) return [];

      // Get conversations for current user
      const { data: userConversations, error: convError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations!inner(*)
        `)
        .eq('employee_id', currentEmployeeId)
        .eq('is_active', true);

      if (convError) throw convError;

      const conversationIds = userConversations?.map(uc => uc.conversation_id) || [];
      
      if (conversationIds.length === 0) return [];

      // Get full conversation data with participants
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          participants:conversation_participants(
            *,
            employee:employees(id, name, profile_image, position)
          )
        `)
        .in('id', conversationIds)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data as any[]).map(conv => ({
        ...conv,
        participants: conv.participants?.filter((p: any) => p.is_active) || []
      })) as Conversation[];
    },
    enabled: !!currentEmployeeId,
  });

  // Real-time subscription for conversations
  useEffect(() => {
    if (!currentEmployeeId) return;

    const channel = supabase
      .channel('conversations-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['conversations', currentEmployeeId] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_participants'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['conversations', currentEmployeeId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentEmployeeId, queryClient]);

  return {
    conversations,
    isLoading,
    error
  };
};

export const useMessages = (conversationId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: messages = [], isLoading, error } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async (): Promise<Message[]> => {
      if (!conversationId) return [];

      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:employees(id, name, profile_image)
        `)
        .eq('conversation_id', conversationId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data as any[]).map(msg => ({
        ...msg,
        message_type: msg.message_type as 'text' | 'image' | 'file'
      })) as Message[];
    },
    enabled: !!conversationId,
  });

  // Real-time subscription for messages
  useEffect(() => {
    if (!conversationId) return;

    const channel = supabase
      .channel(`messages-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          console.log('New message received:', payload);
          queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, queryClient]);

  const sendMessage = useMutation({
    mutationFn: async (messageData: MessageInput) => {
      const currentEmployeeId = localStorage.getItem('currentEmployeeId');
      if (!currentEmployeeId) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: currentEmployeeId,
          content: messageData.content,
          message_type: messageData.message_type || 'text',
          attachments: messageData.attachments,
          reply_to_id: messageData.reply_to_id
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) => {
      console.error('Error sending message:', error);
      toast({
        title: "خطأ",
        description: "فشل في إرسال الرسالة",
        variant: "destructive"
      });
    }
  });

  return {
    messages,
    isLoading,
    error,
    sendMessage: sendMessage.mutate,
    isSending: sendMessage.isPending
  };
};

export const useCreateConversation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentEmployeeId = localStorage.getItem('currentEmployeeId');

  return useMutation({
    mutationFn: async ({ participantIds, title }: { participantIds: string[], title?: string }) => {
      if (!currentEmployeeId) throw new Error('User not authenticated');

      // Create conversation
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert({
          title,
          is_group: participantIds.length > 1,
          created_by: currentEmployeeId
        })
        .select()
        .single();

      if (convError) throw convError;

      // Add creator as participant
      const allParticipants = [currentEmployeeId, ...participantIds.filter(id => id !== currentEmployeeId)];

      // Add participants
      const { error: participantsError } = await supabase
        .from('conversation_participants')
        .insert(
          allParticipants.map(employeeId => ({
            conversation_id: conversation.id,
            employee_id: employeeId
          }))
        );

      if (participantsError) throw participantsError;

      return conversation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({
        title: "نجح",
        description: "تم إنشاء المحادثة بنجاح"
      });
    },
    onError: (error) => {
      console.error('Error creating conversation:', error);
      toast({
        title: "خطأ", 
        description: "فشل في إنشاء المحادثة",
        variant: "destructive"
      });
    }
  });
};

export const useUnreadMessagesCount = () => {
  const currentEmployeeId = localStorage.getItem('currentEmployeeId');

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unread-messages-count', currentEmployeeId],
    queryFn: async (): Promise<number> => {
      if (!currentEmployeeId) return 0;

      // Get user's conversations first
      const { data: userConversations, error: convError } = await supabase
        .from('conversation_participants')
        .select('conversation_id, last_read_at')
        .eq('employee_id', currentEmployeeId)
        .eq('is_active', true);

      if (convError) throw convError;
      if (!userConversations || userConversations.length === 0) return 0;

      const conversationIds = userConversations.map(uc => uc.conversation_id);

      // Count unread messages
      let totalUnread = 0;
      for (const participant of userConversations) {
        const { data: messages, error } = await supabase
          .from('messages')
          .select('id')
          .eq('conversation_id', participant.conversation_id)
          .neq('sender_id', currentEmployeeId)
          .gt('created_at', participant.last_read_at || '1970-01-01');

        if (!error && messages) {
          totalUnread += messages.length;
        }
      }

      return totalUnread;
    },
    enabled: !!currentEmployeeId,
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  return unreadCount;
};